// components/users/UsersTable.tsx
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Edit,
  Trash2,
  Mail,
  Phone,
  Shield,
  ChevronLeft,
  ChevronRight,
  Users as UsersIcon,
  Lock,
  Send,
  ArrowRightLeft,
} from "lucide-react";
import { ReassignModal } from "@/components/opportunities/ReassignModal";

interface UsersTableProps {
  users: any[];
  onEdit: (user: any) => void;
  onDelete: (user: any) => void;
  onResetPassword: (userId: string, email: string) => void;
  onSendWelcomeEmail: (userId: string, email: string) => void;
  currentUserId?: string;
  slug?: string; // Added slug prop for API calls
}

export function UsersTable({
  users,
  onEdit,
  onDelete,
  onResetPassword,
  onSendWelcomeEmail,
  currentUserId,
  slug = "", // Default to empty string if not provided
}: UsersTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [userToReassign, setUserToReassign] = useState<any>(null);
  const itemsPerPage = 10;

  const totalPages = Math.ceil(users.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = users.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const getInitials = (firstName: string = "", lastName: string = "") => {
    const first = firstName.charAt(0) || "";
    const last = lastName.charAt(0) || "";
    return `${first}${last}`.toUpperCase();
  };

  // Función para obtener el nombre del rol correctamente
  const getRoleName = (role: any) => {
    if (!role) return "Sin rol";

    // Si es un string, devolver el string directamente
    if (typeof role === "string") return role;

    // Si es un objeto con propiedad 'name', devolver el name
    if (role && typeof role === "object" && "name" in role) return role.name;

    // Si es cualquier otra cosa, devolver "Sin rol"
    return "Sin rol";
  };

  // Helper para determinar si está activo
  const isUserActive = (user: any) => {
    if (user.is_active !== undefined) return user.is_active;
    return user.estado !== 0 && user.estado !== false;
  };

  const handleReassignClick = (user: any) => {
    setUserToReassign(user);
    setReassignModalOpen(true);
  };

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Contacto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {paginatedUsers.map((user) => {
                const isCurrentUser = user.id === currentUserId;
                const isActive = isUserActive(user);
                return (
                  <tr key={user.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          {user.avatar_url ? (
                            <img
                              className="h-10 w-10 rounded-full"
                              src={user.avatar_url}
                              alt={`${user.first_name} ${user.last_name}`}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
                              <span className="text-white font-bold">
                                {getInitials(user.first_name, user.last_name)}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-slate-900">
                            {user.first_name} {user.last_name}
                            {isCurrentUser && (
                              <Badge className="ml-2 bg-blue-100 text-blue-800 text-xs">
                                Tú
                              </Badge>
                            )}
                          </div>
                          {user.is_owner && (
                            <Badge className="bg-amber-100 text-amber-800 text-xs mt-1">
                              Propietario
                            </Badge>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-900">
                        <div className="flex items-center">
                          <Mail className="h-4 w-4 mr-2 text-slate-400" />
                          {user.email}
                        </div>
                        {user.phone && (
                          <div className="flex items-center mt-1">
                            <Phone className="h-4 w-4 mr-2 text-slate-400" />
                            <span className="text-sm text-slate-600">
                              {user.phone}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-slate-900">
                        <Shield className="h-4 w-4 mr-2 text-slate-400" />
                        {/* CORREGIDO: Usar getRoleName para manejar string u objeto */}
                        {getRoleName(user.role)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(user)}
                          disabled={isCurrentUser}
                          className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title={
                            isCurrentUser
                              ? "No puedes editar tu propio usuario"
                              : "Editar usuario"
                          }
                        >
                          <Edit className="h-4 w-4" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReassignClick(user)}
                          disabled={!isActive}
                          className="h-8 w-8 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          title="Reasignar cartera de oportunidades"
                        >
                          <ArrowRightLeft className="h-4 w-4" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(user)}
                          disabled={user.is_owner || isCurrentUser || !isActive}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          title={
                            user.is_owner
                              ? "No se puede eliminar al propietario"
                              : isCurrentUser
                                ? "No puedes eliminar tu propio usuario"
                                : !isActive
                                  ? "Usuario ya inactivo"
                                  : "Eliminar usuario"
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {paginatedUsers.length === 0 && (
          <div className="text-center py-12">
            <UsersIcon className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              No hay usuarios
            </h3>
            <p className="text-slate-600">
              No se encontraron usuarios con los filtros aplicados
            </p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Mostrando {startIndex + 1} a {Math.min(endIndex, users.length)} de{" "}
              {users.length} usuarios
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      className="w-8 h-8 p-0"
                    >
                      {page}
                    </Button>
                  ),
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {userToReassign && (
        <ReassignModal
          isOpen={reassignModalOpen}
          onClose={() => {
            setReassignModalOpen(false);
            setUserToReassign(null);
          }}
          type="bulk"
          currentOwner={userToReassign}
          targetUsers={users}
          slug={slug}
          onSuccess={() => {
            // Optional: Trigger a refresh if needed, or just close
            setReassignModalOpen(false);
          }}
        />
      )}
    </Card>
  );
}
